<script setup lang="ts">
import { computed, ref } from 'vue'
import {
  Dialog,
  DialogTrigger,
} from '@/components/ui/dialog'
import type { PackageProps } from '@/types'

const props = withDefaults(defineProps<PackageProps>(), {
  id: '',
  name: 'Internet Package',
  currency: 'KES',
  type: 'PAID',
  quota: 'Unlimited',
  isFeatured: false,
  isBestValue: false,
})

// Animated hover state
const isHovered = ref(false)

function handleSelect(e: MouseEvent) {
  const el = e.target as HTMLElement
  setTimeout(() => {
    el.blur();
    (document.activeElement as HTMLElement).blur()
    const phoneInput = document.getElementById('phone') as HTMLElement
    if (phoneInput) {
      phoneInput.addEventListener('focusin', (e) => {
        const ph = e.target as HTMLElement
        if (document.activeElement !== ph)
          ph.focus()
      }, true)

      phoneInput.focus()
      phoneInput.click()
      phoneInput.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }

    if (props.onSelect)
      props.onSelect()
  }, 200)
}

function updateOpen(open: boolean) {
  if (props.handleDialogClose)
    props.handleDialogClose(open)
}

// Determine icon based on package type
const packageIcon = computed(() => {
  switch (props.type) {
    case 'AD':
      return 'i-material-symbols-movie-info-outline-rounded'
    case 'FREE':
      return 'i-solar-gift-linear'
    default:
      return 'i-solar-global-linear'
  }
})

// Format the package title
const packageTitle = computed(() => {
  return props.name || `${props.quota} - ${props.duration}`
})

// Format the button text based on package type
const buttonText = computed(() => {
  switch (props.type) {
    case 'AD':
      return 'Watch Ad'
    case 'FREE':
      return 'Connect'
    default:
      return 'Buy Now'
  }
})
</script>

<template>
  <div
    class="relative flex flex-col justify-between bg-background/70 rounded-xl p-4 border border-border transition-all duration-300 mt-2 hover:shadow-md hover:border-primary/20"
    :class="{
      'ring-2 ring-primary/30 border-primary/20': isFeatured,
      'transform hover:-translate-y-1': !isHovered,
    }"
    @mouseenter="isHovered = true"
    @mouseleave="isHovered = false"
  >
    <!-- Best value badge -->
    <div
      v-if="isBestValue"
      class="absolute -top-3 left-1/2 transform -translate-x-1/2 bg-primary text-secondary text-10px font-medium py-1 px-3 rounded-full"
    >
      Best Value
    </div>

    <!-- Header -->
    <div class="flex justify-between items-center">
      <div class="w-7 h-7 rounded-full bg-primary/10 border border-primary/20 flex items-center justify-center text-primary text-md">
        <div :class="packageIcon" />
      </div>
      <div class="text-xs font-medium text-primary border border-border bg-accent rounded-full py-0.5 px-3">
        {{ quota }}
      </div>
    </div>

    <!-- Package info -->
    <div class="flex flex-col items-start mt-2">
      <div class="font-semibold text-xs mb-1">
        {{ packageTitle }}
      </div>
      <div class="text-xs text-muted-foreground">
        {{ duration }}  • {{ devices }} device(s)
      </div>
    </div>

    <!-- Price and button -->
    <div class="flex flex-col items-start gap-1 mt-1">
      <div
        class="text-xl font-bold flex items-center"
        :class="{ 'text-primary': isFeatured }"
      >
        <template v-if="type === 'FREE'">
          Free
        </template>
        <template v-else>
          {{ price }} <span class="text-sm ml-1 font-normal text-muted-foreground">{{ currency }}</span>
        </template>
      </div>

      <Dialog @update:open="updateOpen">
        <DialogTrigger as-child>
          <button
            id="dialog-trigger-button"
            class="bg-primary/90 w-full text-primary-foreground px-4 py-1.5 rounded-full text-xs font-medium transition-all duration-200 hover:bg-primary flex items-center justify-center gap-2"
            @click="handleSelect"
          >
            {{ buttonText }}
            <div class="i-solar-arrow-right-linear" />
          </button>
        </DialogTrigger>
        <slot />
      </Dialog>
    </div>
  </div>
</template>
